import {
	Box,
	Button,
	Inline,
	Icons,
	Select,
	Table,
	Stack,
	Text,
	TableDataCell,
	useTimeout,
	Money,
	Alert,
	Heading,
	Component,
	RadioInput,
} from "@sembark-travel/ui-base"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR, isAbortError } from "@sembark-travel/xhr"
import React, { useRef, useEffect, useState, useMemo } from "react"
import * as Validator from "yup"
import { IDaywisePrice } from "../utils"
import {
	createDuration,
	dateToUTCString,
	formatDate,
	isSame,
	parseDate,
	startOf,
} from "@sembark-travel/datetime-utils"
import {
	TextInputField,
	SelectField,
	GetFieldValue,
	useFieldValue,
	useForm,
	FieldArray,
	EmptyNumberValidator,
	addKeyToFieldArrayItem,
	getFieldArrayItemKey,
	CheckboxInputField,
} from "@sembark-travel/ui-form"
import {
	TTravelActivity,
	TTravelActivityTicketType,
	TTravelActivityDuration,
	TTravelActivityTicketTouristConfiguration,
	SelectActivity,
	getTouristConfigurationsForPax,
} from "./../TravelActivities"
import { type TChildrenArray, childrenToQueryParams } from "../Tourists"
import { TTripDestination } from "../TripDestinations"
import {
	formatMoneyByDecimal,
	moneyParseByDecimal,
} from "@sembark-travel/money"
import {
	InlineSelectTenantCurrencyInputField,
	useFunctionalCurrencyOfTenant,
} from "../Currencies"
import { Required } from "utility-types"
import { isTicketTypeClosedOnDate } from "../TravelActivities/utils"
import { PickupDropLocationFields } from "../CabScheduling"
import { TTransportServiceLocationPoint } from "../TransportServices"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { Dialog } from "@sembark-travel/ui-dialog"
import { SelectTravelActivityTypes } from "../TravelActivities"
import { EntryTypeIcon } from "../shared/EntryTypeConfig"

export const TravelActivityDatewisePricesValidationSchema =
	Validator.object().shape({
		dates: Validator.array()
			.of(Validator.date().required("Please select a valid day/date"))
			.required("Days field id required")
			.min(1, "Days field is required"),
		no_of_day: EmptyNumberValidator()
			.positive("Days should be a positive integer")
			.typeError("Days should be a number"),
		activity: Validator.object().required("Please select an activity"),
		ticket_type: Validator.object().nullable(),
		duration: Validator.object().nullable(),
		slot: Validator.mixed().nullable(),
		pick_up_location: Validator.mixed().nullable(),
		drop_location: Validator.mixed().nullable(),
		ticket_tourist_configurations: Validator.array()
			.nullable()
			.of(
				Validator.object().shape({
					configuration: Validator.object().required("Please select a type"),
					quantity: EmptyNumberValidator()
						.positive("Quantity should be a positive integer")
						.integer("Quantity should be a positive integer")
						.required("Quantity field is required"),
					complementary: EmptyNumberValidator()
						.min(0, "FOC should be 0 or a positive integer")
						.integer("FOC should be a positive integer")
						.test(
							"less-than-quantity",
							"FOC must be less than Quantity",
							function (value) {
								const { quantity } = this.parent
								if (value == null || quantity == null) return true
								return value < quantity
							}
						),
					date_wise_prices: Validator.array().required("Please provide prices"),
				})
			),
	})

export type TTravelActivityDatewisePricesInputFieldValue = {
	dates?: Array<Date>
	activity?: TTravelActivity
	ticket_type?: TTravelActivityTicketType
	duration?: Pick<TTravelActivityDuration, "iso" | "name">
	activity_slots?: Array<string>
	slot?: string
	has_complementary?: boolean
	pick_up_location?: string | TTransportServiceLocationPoint
	drop_location?: string | TTransportServiceLocationPoint
	use_hotel_as_pickup?: string | null
	use_hotel_as_drop?: string | null
	comments?: string
	ticket_tourist_configurations?: Array<{
		__id: number
		quantity?: number
		complementary?: number
		configuration?: TTravelActivityTicketTouristConfiguration
		fetching_prices?: boolean
		date_wise_prices?: Array<
			IDaywisePrice & {
				per_quantity_price?: number
				per_quantity_given_price?: number
				per_quantity_booked_price?: number
				// Fields for system rate change detection
				saved_system_calculated_price?: number
				saved_manual_given_price?: number
				disable_price_resets?: boolean
			}
		>
	}>
}

export type TTravelActivityDatewisePricesInputFieldArrayValue = Array<
	{
		__id: number
	} & TTravelActivityDatewisePricesInputFieldValue
>

export type TValidTravelActivityDatewisePricesInputFieldValue = Omit<
	Required<TTravelActivityDatewisePricesInputFieldValue, "dates" | "activity">,
	"ticket_tourist_configurations"
> & {
	ticket_tourist_configurations: Array<
		Required<
			Required<
				TTravelActivityDatewisePricesInputFieldValue,
				"ticket_tourist_configurations"
			>["ticket_tourist_configurations"][number],
			"__id" | "configuration" | "configuration"
		>
	>
}

export type TValidTravelActivityDatewisePricesInputFieldArrayValue = Array<
	{
		__id: number
	} & TValidTravelActivityDatewisePricesInputFieldValue
>

export function TravelActivityDatewisePricesInputFieldArray({
	name,
	hideTitle,
	canCustomizeDuration = false,
	...props
}: {
	name: string
	hideTitle?: boolean
	canCustomizeDuration?: boolean
} & Omit<
	React.ComponentProps<typeof TravelActivityDatewisePricesInputField>,
	"name" | "onRemove" | "onDuplicate"
>) {
	const form = useForm()
	return (
		<FieldArray<TTravelActivityDatewisePricesInputFieldArrayValue[number]>
			name={name}
		>
			{({ fields }) => (
				<Stack gap="4">
					{fields.length && !hideTitle ? (
						<Box paddingTop="4" paddingBottom="2">
							<Heading fontSize="md" color="primary">
								Activities
							</Heading>
						</Box>
					) : null}
					{fields.map((name, index) => (
						<Box
							key={getFieldArrayItemKey(form, name)}
							paddingLeft="4"
							borderLeftWidth="1"
						>
							<TravelActivityDatewisePricesInputField
								{...props}
								name={name}
								onRemove={() => fields.remove(index)}
								onDuplicate={(value) =>
									fields.push(addKeyToFieldArrayItem(value))
								}
								canCustomizeDuration={canCustomizeDuration}
							/>
						</Box>
					))}
					<Box paddingY="4">
						<Button
							onClick={() =>
								fields.push(
									addKeyToFieldArrayItem({
										activity: undefined,
										ticket_type: undefined,
										duration: undefined,
										has_complementary: false,
										ticket_tourist_configurations: [],
										dates: [],
									})
								)
							}
							size="sm"
							status="primary"
							level="secondary"
						>
							<Icons.Plus /> Add {fields.length ? "More " : ""}Activities
						</Button>
					</Box>
				</Stack>
			)}
		</FieldArray>
	)
}

export function TravelActivityDatewisePricesInputField({
	name,
	currency: propCurrency,
	noOfAdults,
	children,
	dates,
	tripDestinations,
	onRemove,
	onDuplicate,
	showBookedPrices,
	canCustomizeDuration,
	borderBottomWidth = "1",
	allowAddingPickupDrop = false,
	activityCreatables = {
		activity: true,
		activityType: true,
	},
}: {
	name: string
	currency?: string
	noOfAdults: number
	children: TChildrenArray
	dates: Array<Date> | undefined
	tripDestinations: Array<TTripDestination>
	onRemove?: () => void
	onDuplicate?: (value: TTravelActivityDatewisePricesInputFieldValue) => void
	showBookedPrices?: boolean
	canCustomizeDuration?: boolean
	borderBottomWidth?: React.ComponentProps<typeof Stack>["borderBottomWidth"]
	allowAddingPickupDrop?: boolean
	activityCreatables?: {
		activity: boolean
		activityType: boolean
	}
}) {
	const form = useForm()
	const functionalCurrency = useFunctionalCurrencyOfTenant()
	const [forceUpdate, setForceUpdate] = useState<number>(0)
	const currency = useMemo(() => {
		if (propCurrency) return propCurrency
		return (
			tripDestinations.map((d) => d.currency).at(0) ||
			functionalCurrency.currency
		)
	}, [propCurrency, tripDestinations, functionalCurrency])
	return (
		<Stack gap="4" borderBottomWidth={borderBottomWidth} paddingBottom="4">
			<ActivityDatesInputField name={`${name}.dates`} syncWithDates={dates} />
			<Inline gap="6">
				<Inline gap="4" flex="1" flexWrap="wrap" minWidth="0">
					<Inline flex="1" gap="4" width="full" flexWrap="wrap">
						<Stack gap="4" flex="1" width="full">
							<SelectField
								key={forceUpdate}
								label="Name"
								select={SelectActivity}
								tripDestinations={tripDestinations}
								name={`${name}.activity`}
								creatable={activityCreatables.activity}
								onChange={(value: TTravelActivity | undefined) => {
									// finally, set the activity
									form.change(`${name}.activity` as never, value)
									form.change(
										`${name}.ticket_type` as never,
										value?.ticket_types?.at(0)
									)
									if (allowAddingPickupDrop) {
										form.batch(() => {
											form.change(
												`${name}.pick_up_location` as never,
												value?.ticket_types?.at(0)?.pick_up_locations?.at(0)
											)
											form.change(
												`${name}.drop_location` as never,
												value?.ticket_types?.at(0)?.drop_locations?.at(0)
											)
											form.change(
												`${name}.use_hotel_as_pickup` as never,
												value?.ticket_types?.at(0)?.set_hotel_as_default_pickup
											)
											form.change(
												`${name}.use_hotel_as_drop` as never,
												value?.ticket_types?.at(0)?.set_hotel_as_default_drop
											)
										})
									}

									if (value?.ticket_types?.at(0)?.durations?.at(0)?.iso) {
										form.change(
											`${name}.duration` as never,
											value?.ticket_types?.at(0)?.durations?.at(0)
										)
									} else {
										form.change(`${name}.duration` as never, undefined)
									}

									if (
										value?.ticket_types?.at(0)?.durations?.at(0)?.slots
											?.length === 1
									) {
										form.change(
											`${name}.slot` as never,
											value?.ticket_types?.at(0)?.durations?.at(0)?.slots?.at(0)
										)
									} else {
										form.change(`${name}.slot` as never, undefined)
									}

									if (
										value?.ticket_types?.at(0)?.durations?.at(0)?.slots?.length
									) {
										form.change(
											`${name}.activity_slots` as never,
											value?.ticket_types?.at(0)?.durations?.at(0)?.slots
										)
									} else {
										form.change(`${name}.activity_slots` as never, undefined)
									}
									// reset the tickets
									form.change(
										`${name}.ticket_tourist_configurations` as never,
										undefined
									)
								}}
								help={
									<GetFieldValue<TTravelActivity | undefined>
										name={`${name}.activity`}
									>
										{({ value }) =>
											value?.deleted_at ? (
												<Box>
													<Alert status="warning" inline>
														Activity disabled. Please update.
													</Alert>
												</Box>
											) : null
										}
									</GetFieldValue>
								}
							/>
							<Inline flexWrap="wrap" gap="4" collapseBelow="sm">
								<GetFieldValue<TTravelActivity | undefined>
									name={`${name}.activity`}
								>
									{({ value }) =>
										value && value.ticket_types?.length ? (
											<Inline gap={"2"}>
												<Component initialState={false}>
													{({
														state: showDialog,
														setState: changeDialogState,
													}) => {
														const onTicketTypeChange = (
															value: TTravelActivityTicketType | undefined
														) => {
															form.batch(() => {
																form.change(
																	`${name}.ticket_type` as never,
																	value
																)
																if (allowAddingPickupDrop) {
																	form.change(
																		`${name}.pick_up_location` as never,
																		value?.pick_up_locations?.at(0)
																	)
																	form.change(
																		`${name}.drop_location` as never,
																		value?.drop_locations?.at(0)
																	)
																	form.change(
																		`${name}.use_hotel_as_pickup` as never,
																		value?.set_hotel_as_default_pickup
																	)
																	form.change(
																		`${name}.use_hotel_as_drop` as never,
																		value?.set_hotel_as_default_drop
																	)
																}

																if (value?.durations?.at(0)?.iso) {
																	form.change(
																		`${name}.duration` as never,
																		value?.durations?.at(0)
																	)
																} else {
																	form.change(
																		`${name}.duration` as never,
																		undefined
																	)
																}
																if (value?.durations?.at(0)?.slots?.length) {
																	form.change(
																		`${name}.activity_slots` as never,
																		value?.durations?.at(0)?.slots
																	)
																} else {
																	form.change(
																		`${name}.activity_slots` as never,
																		undefined
																	)
																}

																if (
																	value?.durations?.length &&
																	value.durations[0].slots?.length === 1
																) {
																	form.change(
																		`${name}.slot` as never,
																		value.durations[0].slots[0]
																	)
																} else {
																	form.change(
																		`${name}.slot` as never,
																		undefined
																	)
																}
															})
														}
														return (
															<>
																<SelectField
																	secondaryLabel={
																		<Button
																			inline
																			onClick={() => changeDialogState(true)}
																		>
																			<Icons.ArrowExpand />
																		</Button>
																	}
																	multiple={false}
																	label="Ticket/Package Type"
																	select={SelectTravelActivityTypes}
																	name={`${name}.ticket_type`}
																	options={value.ticket_types}
																	activity={value}
																	creatable={
																		activityCreatables.activityType && !!value
																	}
																	clearable={false}
																	fullWidth
																	onCreateSuccess={(travelActivity) => {
																		form.batch(() => {
																			form.change(
																				`${name}.activity` as never,
																				travelActivity as never
																			)
																			setForceUpdate((prev: number) => prev + 1)
																		})
																	}}
																	onChange={onTicketTypeChange}
																	help={
																		<GetFieldValue<
																			TTravelActivityTicketType | undefined
																		>
																			name={`${name}.ticket_type`}
																		>
																			{({ value }) => {
																				if (!value) {
																					return null
																				}
																				return (dates || []).some((date) =>
																					isTicketTypeClosedOnDate(value, date)
																				) ? (
																					<Box>
																						<Alert
																							status="error"
																							title="Activity/Ticket Closed."
																						>
																							<Text>
																								Closed on{" "}
																								{(dates || [])
																									.filter((date) =>
																										isTicketTypeClosedOnDate(
																											value,
																											date
																										)
																									)
																									.map((d) =>
																										formatDate(
																											d,
																											"dddd, DD MMM"
																										)
																									)
																									.join(", ")}
																								.
																							</Text>
																						</Alert>
																					</Box>
																				) : value?.deleted_at ? (
																					<Box>
																						<Alert status="warning" inline>
																							Ticket disabled. Please update.
																						</Alert>
																					</Box>
																				) : null
																			}}
																		</GetFieldValue>
																	}
																/>

																<GetFieldValue<
																	TTravelActivityTicketType | undefined
																>
																	name={`${name}.ticket_type`}
																>
																	{({ value: selectedTicketType }) => (
																		<Dialog
																			open={showDialog}
																			title="Activity Type Details"
																			onClose={() => changeDialogState(false)}
																			sm
																		>
																			<Dialog.Body>
																				<Stack
																					borderWidth="1"
																					rounded="md"
																					overflow="hidden"
																				>
																					{(value.ticket_types
																						? value.ticket_types
																						: []
																					).map(function (ticketType, index) {
																						return (
																							<Inline
																								key={ticketType.name}
																								as="label"
																								paddingY="2"
																								paddingX="4"
																								borderTopWidth={
																									index !== 0 ? "1" : undefined
																								}
																								bgColor={
																									ticketType.id ===
																									selectedTicketType?.id
																										? "primary"
																										: {
																												hover: "subtle",
																											}
																								}
																							>
																								<Stack
																									flex="1"
																									minWidth="0"
																									gap="px"
																								>
																									<Inline gap={"2"}>
																										<Text>
																											{ticketType.display_name}
																										</Text>
																										<Box>
																											<EntryTypeIcon
																												entryType={
																													ticketType?.entry_type
																												}
																												createdBy={
																													ticketType?.created_by
																														?.name
																												}
																											/>
																										</Box>
																									</Inline>
																									{ticketType.short_name ? (
																										<Text
																											color={"muted"}
																											fontWeight="normal"
																										>
																											{ticketType.short_name}
																										</Text>
																									) : null}
																								</Stack>
																								<Box>
																									<RadioInput
																										name={`${name}.ticket_type.id`}
																										checked={
																											ticketType.id ===
																											selectedTicketType?.id
																										}
																										value={ticketType.id}
																										onChange={() => {
																											onTicketTypeChange(
																												ticketType
																											)
																										}}
																									/>
																								</Box>
																							</Inline>
																						)
																					})}
																				</Stack>
																			</Dialog.Body>
																			<Dialog.Footer>
																				<Button
																					onClick={() =>
																						changeDialogState(false)
																					}
																				>
																					Done
																				</Button>
																			</Dialog.Footer>
																		</Dialog>
																	)}
																</GetFieldValue>
															</>
														)
													}}
												</Component>

												<GetFieldValue<TTravelActivityTicketType | undefined>
													name={`${name}.ticket_type`}
												>
													{({ value: ticketType }) =>
														ticketType ? (
															<Box paddingTop={"8"}>
																<EntryTypeIcon
																	entryType={ticketType?.entry_type}
																	createdBy={ticketType.created_by?.name}
																/>
															</Box>
														) : null
													}
												</GetFieldValue>
											</Inline>
										) : null
									}
								</GetFieldValue>
								<Inline gap="4">
									<GetFieldValue<string[] | undefined>
										name={`${name}.activity_slots`}
									>
										{({ value }) =>
											canCustomizeDuration ? (
												<Box
													style={{
														width: "110px",
													}}
												>
													<ActivitySlotInputField
														activityFieldName={name}
														options={value || undefined}
														creatable={canCustomizeDuration}
													/>
												</Box>
											) : null
										}
									</GetFieldValue>
									<GetFieldValue<TTravelActivityTicketType | undefined>
										name={`${name}.ticket_type`}
									>
										{({ value }) =>
											(value && value.durations?.length) ||
											canCustomizeDuration ? (
												<Inline gap="4">
													<Box
														style={{
															width: "110px",
														}}
													>
														<ActivityDurationInputField
															options={value?.durations?.filter((d) => d.iso)}
															activityFieldName={name}
															creatable={canCustomizeDuration}
														/>
													</Box>
												</Inline>
											) : null
										}
									</GetFieldValue>
								</Inline>
								{allowAddingPickupDrop ? (
									<GetFieldValue<TTravelActivityTicketType | undefined>
										name={`${name}.ticket_type`}
									>
										{({ value: selectedTicketType }) => (
											<PickupDropLocationFields
												namePrefix={name}
												pickupLocations={
													selectedTicketType?.pick_up_locations || []
												}
												dropLocations={selectedTicketType?.drop_locations || []}
												colProps={{ xs: 12, sm: 6, lg: 4 }}
												gridProps={{ gap: "6" }}
											/>
										)}
									</GetFieldValue>
								) : null}
							</Inline>
						</Stack>
						<ActivityTouristConfigurationsField
							name={name}
							defaultCurrency={currency}
							noOfAdults={noOfAdults}
							children={children}
							showBookedPrices={showBookedPrices}
						/>
					</Inline>
				</Inline>
				{onRemove || onDuplicate ? (
					<Box paddingTop="6">
						<Stack gap="4">
							{onRemove ? (
								<Box>
									<Button onClick={() => onRemove()} size="sm">
										<Icons.Cancel />
									</Button>
								</Box>
							) : null}
							{onDuplicate ? (
								<GetFieldValue<TTravelActivityDatewisePricesInputFieldValue>
									name={name}
								>
									{({ value }) =>
										value && value.activity ? (
											<Box>
												<Button onClick={() => onDuplicate(value)} size="sm">
													<Icons.Duplicate />
												</Button>
											</Box>
										) : null
									}
								</GetFieldValue>
							) : null}
						</Stack>
					</Box>
				) : null}
			</Inline>
		</Stack>
	)
}

function ActivityDatesInputField({
	name,
	syncWithDates,
}: {
	name: string
	syncWithDates: Array<Date> | undefined
}) {
	const { onChange } = useFieldValue<Array<Date> | undefined>(name)
	useEffect(() => {
		// sync the field value with given dates
		const handler = setTimeout(() => {
			onChange(syncWithDates)
		})
		return () => clearTimeout(handler)
	}, [onChange, syncWithDates])
	return null
}

function ActivityDurationInputField({
	activityFieldName,
	options,
	creatable,
}: {
	activityFieldName: string
	options?: Array<TTravelActivityDuration>
	creatable?: boolean
}) {
	return (
		<SelectField
			minWidth="auto"
			label="Duration"
			select={Select}
			name={`${activityFieldName}.duration`}
			options={options}
			creatable={Boolean(creatable)}
			placeholder="60 Mins"
			createOptionLabel={(q) => q}
			onCreateNew={(query) => {
				let durationISO = ""
				try {
					durationISO = createDuration(
						query.replace(/[^\d]/g, ""),
						"minutes"
					).toISOString()
				} catch (e) {
					durationISO = ""
				}
				let duration: TTravelActivityDuration | undefined = undefined
				if (durationISO) {
					duration = {
						iso: durationISO,
						name: createDuration(durationISO).asMinutes() + " Mins",
						slots: [],
					}
				}
				return duration
			}}
		/>
	)
}

function ActivitySlotInputField({
	activityFieldName,
	options,
	creatable,
}: {
	activityFieldName: string
	options?: Array<string>
	creatable?: boolean
}) {
	return (
		<SelectField
			minWidth="auto"
			label="Slot"
			select={Select}
			name={`${activityFieldName}.slot`}
			options={options}
			clearable={true}
			placeholder="14:00"
			creatable={Boolean(creatable)}
			createOptionLabel={(q) => q}
			closeOnSelect
			onCreateNew={(query) => {
				let date
				try {
					date = parseDate(query, "HH:mm")
				} catch (e) {
					date = undefined
				}
				return date ? formatDate(date, "HH:mm") : ""
			}}
		/>
	)
}

function ActivityTouristConfigurationsField({
	name: activityFieldName,
	defaultCurrency,
	noOfAdults,
	children,
	showBookedPrices,
}: {
	name: string
	defaultCurrency: string
	noOfAdults: number
	children: TChildrenArray
	showBookedPrices?: boolean
}) {
	const { value: activity } = useFieldValue<
		TTravelActivityDatewisePricesInputFieldValue["activity"]
	>(`${activityFieldName}.activity`)
	const { value: ticket_type } = useFieldValue<
		TTravelActivityDatewisePricesInputFieldValue["ticket_type"]
	>(`${activityFieldName}.ticket_type`)
	const { value: dates } = useFieldValue<
		TTravelActivityDatewisePricesInputFieldValue["dates"]
	>(`${activityFieldName}.dates`)
	const { value: existingComments } = useFieldValue<
		TTravelActivityDatewisePricesInputFieldValue["comments"]
	>(`${activityFieldName}.comments`)
	const [refreshCounter, setRefreshCounter] = useState<number>(0)
	const form = useForm()
	const [commentsVisible, showAddComments] = useState<boolean>(
		Boolean(existingComments?.length)
	)
	if (!activity) return null
	const { ticket_tourist_configurations } = activity
	return (
		<Stack gap="1">
			<Inline gap="4" justifyContent="between" alignItems="center">
				<Text fontWeight="semibold">
					Tickets and Prices
					{dates?.length === 1
						? ` - ${formatDate(dates[0], "dddd, D MMM")}`
						: null}
				</Text>
				{dates?.length ? (
					<Inline gap="4" alignItems="center">
						<GetFieldValue<boolean>
							name={`${activityFieldName}.has_complementary`}
						>
							{({ value: has_complementary }) => (
								<Inline title="Free of Cost" alignItems="center" gap="1">
									<CheckboxInputField
										name={`${activityFieldName}.has_complementary`}
										color="muted"
										label="FOC"
										disabled={has_complementary}
									/>
								</Inline>
							)}
						</GetFieldValue>
						{!commentsVisible ? (
							<Button size="sm" inline onClick={() => showAddComments(true)}>
								<Icons.Annotation /> Comments
							</Button>
						) : null}
						<GetFieldValue<
							TTravelActivityDatewisePricesInputFieldValue["ticket_tourist_configurations"]
						>
							name={`${activityFieldName}.ticket_tourist_configurations`}
						>
							{({ value }) => {
								const fetchingPrices = (value || []).some(
									(c) => c.fetching_prices
								)
								return (
									<Button
										inline
										size="sm"
										title="Refresh Prices"
										onClick={() => setRefreshCounter((counter) => counter + 1)}
									>
										<Icons.Refresh
											spin={fetchingPrices}
											opacity={fetchingPrices ? "50" : undefined}
										/>
									</Button>
								)
							}}
						</GetFieldValue>
					</Inline>
				) : null}
			</Inline>
			<GetFieldValue<boolean> name={`${activityFieldName}.has_complementary`}>
				{({ value: has_complementary }) => (
					<FieldArray<
						Required<TTravelActivityDatewisePricesInputFieldValue>["ticket_tourist_configurations"][number]
					>
						name={`${activityFieldName}.ticket_tourist_configurations`}
					>
						{({ fields }) => (
							<Stack gap="2">
								<Table
									headers={[
										<Inline
											alignItems="center"
											gap="4"
											justifyContent="between"
										>
											<Text>Type</Text>
											{ticket_tourist_configurations?.length ? (
												<Button
													title="Add More Ticket Types for this Activity"
													onClick={() =>
														fields.push(
															addKeyToFieldArrayItem({
																quantity: 1,
																complementary: 0,
																configuration: ticket_tourist_configurations[0],
																date_wise_prices: [],
															})
														)
													}
													inline
													level="tertiary"
												>
													<Icons.Plus />
												</Button>
											) : null}
										</Inline>,
										"Qty.",
									]
										.concat(
											has_complementary
												? [<Box title="Free of Cost">FOC</Box>]
												: []
										)
										.concat((dates?.length || 0) > 1 ? ["Date"] : [])
										.concat([
											"Rate",
											showBookedPrices ? "Booked" : "Given",
											"",
										])}
									bordered
								>
									{fields.map((name, index) => (
										<tbody key={getFieldArrayItemKey(form, name)}>
											<RatesForTouristConfiguration
												activity={activity}
												ticketType={ticket_type}
												configFieldName={name}
												dates={dates}
												key={refreshCounter}
												isBooking={Boolean(showBookedPrices)}
											/>
											<ResetEditedGivenPriceFieldOnConfigChange
												name={name}
												dates={dates}
												ticketType={ticket_type}
												defaultCurrency={defaultCurrency}
											/>
											{!dates?.length ? null : (
												<>
													<FieldArray name={`${name}.date_wise_prices`}>
														{({ fields: dateWisePricesFields }) =>
															dateWisePricesFields.map(
																(dateWisePriceFieldName, row_index) => {
																	return (
																		<tr key={dateWisePriceFieldName}>
																			<TableDataCell>
																				{row_index === 0 ? (
																					<Box style={{ width: "130px" }}>
																						<SelectField
																							select={Select}
																							options={
																								ticket_tourist_configurations
																							}
																							name={`${name}.configuration`}
																							minWidth="100px"
																							size="sm"
																							clearable={false}
																						/>
																					</Box>
																				) : null}
																			</TableDataCell>
																			<TableDataCell>
																				{row_index === 0 ? (
																					<TextInputField
																						size="sm"
																						name={`${name}.quantity`}
																						style={{
																							width: "60px",
																						}}
																						type="number"
																						placeholder="1"
																						min={1}
																					/>
																				) : null}
																			</TableDataCell>
																			{has_complementary ? (
																				<TableDataCell>
																					<GetFieldValue<TTravelActivityTicketTouristConfiguration>
																						name={`${name}.configuration`}
																					>
																						{({ value: configuration }) =>
																							row_index === 0 ? (
																								<TextInputField
																									size="sm"
																									name={`${name}.complementary`}
																									style={{
																										width: "60px",
																									}}
																									type="number"
																									placeholder="0"
																									disabled={
																										configuration.tourist_type ===
																										"group"
																									}
																									min={0}
																									onBlur={(
																										e: React.FocusEvent<HTMLInputElement>
																									) => {
																										if (
																											e.currentTarget.value ===
																											""
																										) {
																											form.change(
																												`${name}.complementary` as never,
																												0 as never
																											)
																										}
																									}}
																								/>
																							) : null
																						}
																					</GetFieldValue>
																				</TableDataCell>
																			) : null}
																			{(dates || []).length > 1 ? (
																				<TableDataCell>
																					{dates[row_index] ? (
																						<Text>
																							{formatDate(
																								dates[row_index],
																								"D MMM"
																							)}
																						</Text>
																					) : null}
																				</TableDataCell>
																			) : null}
																			<GetFieldValue<string>
																				name={`${dateWisePriceFieldName}.currency`}
																			>
																				{({ value: currency }) => {
																					return (
																						<>
																							<TableDataCell>
																								<GetFieldValue<
																									number | undefined
																								>
																									name={`${dateWisePriceFieldName}.per_quantity_price`}
																								>
																									{({ value }) =>
																										value ? (
																											<Money
																												currency={currency}
																												amount={value}
																												showCurrency
																											/>
																										) : (
																											<Box>
																												<Box
																													as="span"
																													color="muted"
																													fontSize="xs"
																													verticalAlign="super"
																												>
																													<InlineSelectTenantCurrencyInputField
																														name={`${dateWisePriceFieldName}.currency`}
																														noCaret
																														defaultValue={
																															defaultCurrency
																														}
																													/>
																												</Box>{" "}
																												<Text
																													color="warning"
																													title="Not Available"
																													as="span"
																												>
																													N/A
																												</Text>
																											</Box>
																										)
																									}
																								</GetFieldValue>
																							</TableDataCell>
																							<TableDataCell>
																								<GetFieldValue<
																									number | undefined
																								>
																									name={`${name}.quantity`}
																								>
																									{({ value }) => (
																										<GetFieldValue<
																											number | undefined
																										>
																											name={`${name}.complementary`}
																										>
																											{({
																												value: complementary,
																											}) =>
																												!showBookedPrices ? (
																													<DatewiseGivenPriceForTouristConfiguration
																														name={`${dateWisePriceFieldName}`}
																														configFieldName={
																															name
																														}
																														quantity={value}
																														complementary={
																															complementary
																														}
																														currency={currency}
																													/>
																												) : (
																													<DatewiseBookedPriceForTouristConfiguration
																														name={`${dateWisePriceFieldName}`}
																														quantity={value}
																														complementary={
																															complementary
																														}
																														currency={currency}
																													/>
																												)
																											}
																										</GetFieldValue>
																									)}
																								</GetFieldValue>
																							</TableDataCell>
																						</>
																					)
																				}}
																			</GetFieldValue>
																			<TableDataCell>
																				{row_index === 0 ? (
																					<Box
																						opacity={
																							(fields.length || 0) <= 1
																								? "0"
																								: "100"
																						}
																					>
																						<Button
																							onClick={() =>
																								fields.remove(index)
																							}
																							inline
																							disabled={
																								(fields.length || 0) <= 1
																							}
																						>
																							<Icons.Cancel />
																						</Button>
																					</Box>
																				) : null}
																			</TableDataCell>
																		</tr>
																	)
																}
															)
														}
													</FieldArray>
												</>
											)}
										</tbody>
									))}
								</Table>
							</Stack>
						)}
					</FieldArray>
				)}
			</GetFieldValue>
			{commentsVisible ? (
				<TextInputField
					name={`${activityFieldName}.comments`}
					label="Comments"
					type="text"
					size="sm"
					placeholder="Any comments regarding prices..."
				/>
			) : null}
			<SetupTouristsConfigurationForPax
				name={activityFieldName}
				children={children}
				noOfAdults={noOfAdults}
			/>
		</Stack>
	)
}

function SetupTouristsConfigurationForPax({
	name,
	noOfAdults,
	children,
}: {
	name: string
	noOfAdults: number
	children: TChildrenArray
}) {
	const { value } =
		useFieldValue<TTravelActivityDatewisePricesInputFieldValue>(name)
	const { activity, ticket_tourist_configurations } = value || {}
	const hasTouristsConfigurationAttached = Boolean(
		ticket_tourist_configurations?.length
	)
	const lastSavedTicketTouristConfigurations = useRef(
		ticket_tourist_configurations
	)

	// Track activity to detect activity changes vs guest/quantity changes
	const lastActivity = useRef(activity?.id)

	if (ticket_tourist_configurations) {
		lastSavedTicketTouristConfigurations.current = ticket_tourist_configurations
	}

	const form = useForm()
	const paxRef = useRef({
		noOfAdults,
		children,
		hasNoOfAdultsChanged: false,
		hasChildrenChanged: false,
	})

	// reset the config if pax changes
	useEffect(() => {
		const hasNoOfAdultsChanged = noOfAdults !== paxRef.current.noOfAdults
		const hasChildrenChanged =
			childrenToQueryParams(paxRef.current.children) !==
			childrenToQueryParams(children)

		if (hasNoOfAdultsChanged || hasChildrenChanged) {
			paxRef.current.noOfAdults = noOfAdults
			paxRef.current.children = children
			paxRef.current.hasNoOfAdultsChanged = hasNoOfAdultsChanged
			paxRef.current.hasChildrenChanged = hasChildrenChanged
			// reset the configuration
			form.change(`${name}.ticket_tourist_configurations`, undefined)
		}
	}, [noOfAdults, children, form, name])

	// create a ticket configuration if not already exists
	useEffect(() => {
		if (!hasTouristsConfigurationAttached && activity) {
			// add tourist configurations
			const touristsConfigurations = getTouristConfigurationsForPax(
				activity.ticket_tourist_configurations,
				noOfAdults,
				children
			)
			if (!touristsConfigurations.length) {
				touristsConfigurations.push({
					quantity: 1,
					configuration: activity.ticket_tourist_configurations[0],
				})
			}
			const handler = setTimeout(() => {
				// Detect if this is an activity change vs guest/quantity change,
				// because this handler will always run when there is activity change or there is changed on pax details of the trip (from update basic details modal) which results in re-creation of the ticket_tourist_configurations
				const isActivityChanged = activity?.id !== lastActivity.current

				const touristsConfigurationsFieldValue: TTravelActivityDatewisePricesInputFieldValue["ticket_tourist_configurations"] =
					touristsConfigurations.map((config) =>
						addKeyToFieldArrayItem({
							...config,
							complementary:
								(config.configuration.tourist_type === "adult" &&
									paxRef.current.hasNoOfAdultsChanged) ||
								(config.configuration.tourist_type === "child" &&
									paxRef.current.hasChildrenChanged)
									? 0
									: lastSavedTicketTouristConfigurations.current?.find(
											(c) => c.configuration?.id === config.configuration.id
										)?.complementary || 0,
							date_wise_prices:
								lastSavedTicketTouristConfigurations.current
									?.find((c) => c.configuration?.id === config.configuration.id)
									?.date_wise_prices?.map((price) => ({
										...price,
										// Reseting the last quote' acitivity saved prices to prevent rate detection logic to run when acitivity is changed and pax config is re-created.
										saved_system_calculated_price: undefined,
										saved_manual_given_price: undefined,
										// Only reset edited_given_price for activity changes
										// Preserve manual overrides for guest/quantity changes
										edited_given_price: isActivityChanged
											? false
											: price.edited_given_price,
										// reset edited_booked_price (negotiated supplier rates in the booking flow on the "Service booking" page for a quote)
										// Note - This same component is used in the booking flow on the "Service booking" page for a quote (always make changes in this component by keeping in mind of both the flows, as both(quote flow and booking flow) serves different use-cases from business point-of-view)
										edited_booked_price: isActivityChanged
											? false
											: price.edited_booked_price,
									})) || [],
						})
					)
				form.change(
					`${name}.ticket_tourist_configurations`,
					touristsConfigurationsFieldValue
				)

				lastActivity.current = activity?.id
				paxRef.current.hasNoOfAdultsChanged = false
				paxRef.current.hasChildrenChanged = false
			})
			return () => clearTimeout(handler)
		}
	}, [
		form,
		activity,
		hasTouristsConfigurationAttached,
		children,
		noOfAdults,
		name,
	])
	return null
}

function RatesForTouristConfiguration({
	activity,
	ticketType,
	duration,
	configFieldName,
	dates,
	isBooking,
}: {
	activity: TTravelActivity
	ticketType?: TTravelActivityTicketType
	duration?: TTravelActivityDuration
	configFieldName: string
	dates?: Array<Date>
	isBooking: boolean
}) {
	const { value: quantity } = useFieldValue<
		Required<TTravelActivityDatewisePricesInputFieldValue>["ticket_tourist_configurations"][number]["quantity"]
	>(`${configFieldName}.quantity`)
	const { value: complementary } = useFieldValue<
		Required<TTravelActivityDatewisePricesInputFieldValue>["ticket_tourist_configurations"][number]["complementary"]
	>(`${configFieldName}.complementary`)
	const { value: configuration } = useFieldValue<
		Required<TTravelActivityDatewisePricesInputFieldValue>["ticket_tourist_configurations"][number]["configuration"]
	>(`${configFieldName}.configuration`)
	const form = useForm()
	const xhr = useXHR()
	const abortController = useRef<AbortController | undefined>(undefined)
	const { set: setTimer, clear: clearTimer } = useTimeout()
	useEffect(() => {
		// abort any pending requests
		abortController.current = new AbortController()
		if (!dates?.length || !configuration) {
			abortController.current?.abort()
			clearTimer()
			return () => undefined
		}
		setTimer(() => {
			// fetch the prices and update
			Promise.resolve()
				.then(async () => {
					form.change(`${configFieldName}.fetching_prices`, 1)
					const prices = await Promise.all(
						dates.map((date) =>
							xhr
								.get<{
									data: {
										price: number | null
										per_quantity_price: number | null
										currency: string | null
									}
								}>(`/travel-activity-prices/calculate`, {
									params: {
										date: dateToUTCString(date),
										date_local: formatDate(date, "YYYY-MM-DD"),
										activity_id: activity.id,
										ticket_tourist_configuration_id: configuration.id,
										ticket_type_id: ticketType?.id,
										quantity,
										complementary,
										is_booking: isBooking ? 1 : 0,
									},
									signal: abortController.current?.signal,
								})
								.then((resp) => resp.data.data)
						)
					)
					abortController.current = undefined
					dates.forEach((date, index) => {
						const per_quantity_price = prices[index].per_quantity_price
						const total_price = prices[index].price
						const currency = prices[index].currency

						form.change(
							`${configFieldName}.date_wise_prices[${index}].date`,
							date
						)
						if (currency) {
							form.change(
								`${configFieldName}.date_wise_prices[${index}].currency`,
								currency
							)
							form.change(
								`${configFieldName}.date_wise_prices[${index}].per_quantity_price`,
								per_quantity_price !== undefined && per_quantity_price !== null
									? formatMoneyByDecimal(
											moneyParseByDecimal(per_quantity_price, currency)
										)
									: undefined
							)
							form.change(
								`${configFieldName}.date_wise_prices[${index}].price`,
								total_price !== undefined && total_price !== null
									? formatMoneyByDecimal(
											moneyParseByDecimal(total_price, currency)
										)
									: undefined
							)
						} else {
							form.change(
								`${configFieldName}.date_wise_prices[${index}].per_quantity_price`,
								undefined
							)
							form.change(
								`${configFieldName}.date_wise_prices[${index}].price`,
								undefined
							)
						}
					})
				})
				.then(() => {
					form.change(`${configFieldName}.fetching_prices`, 0)
				})
				.catch((e) => {
					if (!isAbortError(e)) {
						form.change(`${configFieldName}.fetching_prices`, 0)
						throw e
					}
				})
		}, 300)
		return () => {
			// Cancel any pending fetch
			abortController.current?.abort()
			clearTimer()
		}
	}, [
		activity,
		ticketType,
		duration,
		configuration,
		quantity,
		complementary,
		form,
		dates,
		configFieldName,
		xhr,
		setTimer,
		clearTimer,
		isBooking,
	])
	return null
}

/**
 * Hook to detect system rate changes for travel activity datewise pricing.
 *
 * PURPOSE:
 * Compares saved system rates vs current system rates for travel activities. If the user had manually customized prices
 * AND the system rate has changed since last save, it resets the price to undefined to force review (NewQuoteForm's submission handles to prevent quote save if "given_price" are "undefined").
 *
 * TRIGGER CONDITIONS (both must be true):
 * 1. User manually customized price (saved_manual_given_price !== saved_system_calculated_price)
 * 2. System rate changed since last save (current price !== saved_system_calculated_price)
 *
 * EXAMPLE:
 * - Jan 1: System rate = 2000, User enters 2500 → Saved as: saved_system=2000, saved_manual=2500
 * - Jan 15: User edits quote, System rate now = 3000 (activity pricing changed)
 * - Hook detects both conditions true → Resets per_quantity_given_price AND given_price to undefined
 * - User must review and re-enter price before submitting
 *
 */
function useTravelActivitySystemRateChangeDetection({
	name,
	configFieldName,
	disablePriceResets,
}: {
	name: string
	configFieldName: string
	disablePriceResets: boolean
}) {
	const form = useForm()

	// Current system-calculated rate (from RatesForTouristConfiguration component)
	const { value: price } = useFieldValue<number | undefined | string>(
		`${name}.price`
	)

	const { value: saved_system_calculated_price } = useFieldValue<
		number | undefined | string
	>(`${name}.saved_system_calculated_price`)
	const { value: saved_manual_given_price } = useFieldValue<
		number | undefined | string
	>(`${name}.saved_manual_given_price`)

	// fetching_prices state from RatesForTouristConfiguration component
	// undefined = not started, 1 = fetching, 0 = completed
	const { value: fetching_prices } = useFieldValue<0 | 1 | undefined>(
		`${configFieldName}.fetching_prices`
	)

	const hasDetectedSystemRateChange = useRef(false)

	useEffect(() => {
		// Skip if prices are locked (afterConfirmedQuote = true)
		if (disablePriceResets) {
			hasDetectedSystemRateChange.current = true
			return
		}

		// fetching_prices must be explicitly 0 (fetch completed)
		// undefined = not started yet, 1 = fetching, 0 = completed
		if (fetching_prices !== 0) {
			return
		}

		// Only run once when editing and after system rates are fetched
		if (
			hasDetectedSystemRateChange.current ||
			saved_system_calculated_price === undefined ||
			saved_system_calculated_price === null ||
			saved_system_calculated_price === "" ||
			saved_manual_given_price === undefined ||
			saved_manual_given_price === null ||
			saved_manual_given_price === "" ||
			price === undefined ||
			price === null ||
			price === ""
		) {
			return
		}

		// Check if user had manually customized the price last time
		const wasManuallyCustomized =
			saved_system_calculated_price !== saved_manual_given_price

		// Check if system rate has changed since last save
		const systemRateChanged =
			Number(price || 0) !== saved_system_calculated_price

		// Only reset if BOTH conditions are true
		if (wasManuallyCustomized && systemRateChanged) {
			setTimeout(() => {
				form.batch(() => {
					form.change(`${name}.per_quantity_given_price`, undefined)

					form.change(`${name}.given_price`, undefined)

					//Reseting the last saved prices as no longer needed after rate change detection reset so that it should not trigger again after once.
					form.change(`${name}.saved_system_calculated_price`, undefined)
					form.change(`${name}.saved_manual_given_price`, undefined)
				})

				showSnackbar(
					"System rates have changed. Please review and re-enter prices for affected travel activities.",
					{
						duration: 8000,
						id: "travel_activities_rate_change_detected",
					}
				)

				return () => {
					form.change(`${name}.saved_system_calculated_price`, undefined)
					form.change(`${name}.saved_manual_given_price`, undefined)
				}
			}, 0)
		}

		hasDetectedSystemRateChange.current = true
	}, [
		disablePriceResets,
		saved_system_calculated_price,
		saved_manual_given_price,
		price,
		fetching_prices,
		form,
		name,
	])

	return {
		hasDetectedSystemRateChange,
	}
}

function DatewiseGivenPriceForTouristConfiguration({
	name,
	configFieldName,
	quantity,
	currency,
	complementary,
}: {
	name: string
	configFieldName: string
	quantity?: number
	currency: string
	complementary?: number
}) {
	const form = useForm()
	const { value: price } = useFieldValue<number | undefined>(`${name}.price`)
	const { value: per_quantity_price } = useFieldValue<number | undefined>(
		`${name}.per_quantity_price`
	)
	const { value: per_quantity_given_price } = useFieldValue<number | undefined>(
		`${name}.per_quantity_given_price`
	)
	const { value: edited_given_price } = useFieldValue<boolean | undefined>(
		`${name}.edited_given_price`
	)

	const { value: disable_price_resets } = useFieldValue<boolean | undefined>(
		`${name}.disable_price_resets`
	)

	// Hook to detect system rate changes for travel activity datewise pricing.
	useTravelActivitySystemRateChangeDetection({
		name,
		configFieldName,
		disablePriceResets: disable_price_resets || false,
	})

	useEffect(() => {
		if (!edited_given_price) {
			const handler = setTimeout(() => {
				form.change(`${name}.per_quantity_given_price`, per_quantity_price)
				form.change(`${name}.given_price`, price ?? undefined)
			})
			return () => clearTimeout(handler)
		}
	}, [name, price, per_quantity_price, edited_given_price, form])

	// update the given price when per_quantity_given_price or noOfCabs changes
	useEffect(() => {
		if (edited_given_price) {
			const currentGivenPrice = form.getFieldState(`${name}.given_price`)?.value
			const currentPerQuantityGivenPrice = form.getFieldState(
				`${name}.per_quantity_given_price`
			)?.value
			// If BOTH are undefined, we're in reset state (rate change detection)
			// Preserve undefined for validation error during quote submission
			// This condition prevents the conflict between this useEffect and the useEffect(which handles the "System rate change detection(useTransportServiceSystemRateChangeDetection)" as it changes the "per_quantity_given_price", to "undefined" which causes this useEffect to re-run,
			// which further results in setting the "given_price=0" which overrides intentionlly setted "given_price = undefined" sets by the "useTransportServiceSystemRateChangeDetection - UseEffect")
			if (
				currentPerQuantityGivenPrice === undefined &&
				currentGivenPrice === undefined
			) {
				return // Don't recalculate, preserve undefined as these both will be undefined to handle the case of system rate reset logic
			}
			const given_price =
				per_quantity_given_price !== undefined &&
				(per_quantity_given_price as string | number) !== ""
					? Number((quantity || 0) - (complementary || 0)) *
						Number(per_quantity_given_price)
					: undefined
			const handler = setTimeout(() => {
				form.change(`${name}.given_price`, given_price)
			})
			return () => clearTimeout(handler)
		}
	}, [
		edited_given_price,
		quantity,
		per_quantity_given_price,
		form,
		name,
		complementary,
	])
	const { hasPermission } = useCheckPermissions()
	return (
		<TextInputField
			size="sm"
			name={`${name}.per_quantity_given_price`}
			type="number"
			placeholder="1"
			style={{ maxWidth: "100px" }}
			readOnly={
				!quantity ||
				!(
					hasPermission(PERMISSIONS.MODIFY_QUOTE_GIVEN_RATE) ||
					!per_quantity_price
				)
			}
			help={
				<GetFieldValue<number | undefined> name={`${name}.given_price`}>
					{({ value }) =>
						value && Number(quantity || 0) > 1 ? (
							<Text>
								x {(quantity || 0) - (complementary || 0)} ={" "}
								<Money currency={currency} amount={value} />
							</Text>
						) : null
					}
				</GetFieldValue>
			}
			onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
				const value = e.currentTarget.value ?? undefined
				form.change(`${name}.edited_given_price` as never, true as never)
				form.change(`${name}.per_quantity_given_price` as never, value as never)
			}}
		/>
	)
}

function DatewiseBookedPriceForTouristConfiguration({
	name,
	quantity,
	currency,
	complementary,
}: {
	name: string
	quantity?: number
	currency: string
	complementary?: number
}) {
	const form = useForm()
	const { value: price } = useFieldValue<number | undefined>(`${name}.price`)
	const { value: per_quantity_price } = useFieldValue<number | undefined>(
		`${name}.per_quantity_price`
	)
	const { value: per_quantity_booked_price } = useFieldValue<
		number | undefined
	>(`${name}.per_quantity_booked_price`)
	const { value: edited_booked_price } = useFieldValue<boolean | undefined>(
		`${name}.edited_booked_price`
	)
	useEffect(() => {
		if (!edited_booked_price) {
			const handler = setTimeout(() => {
				form.change(`${name}.per_quantity_booked_price`, per_quantity_price)
				form.change(`${name}.booked_price`, price ?? undefined)
			})
			return () => clearTimeout(handler)
		}
	}, [name, price, per_quantity_price, edited_booked_price, form])

	// update the given price when per_quantity_given_price or noOfCabs changes
	useEffect(() => {
		if (edited_booked_price) {
			const booked_price =
				per_quantity_booked_price !== undefined &&
				(per_quantity_booked_price as string | number) !== ""
					? Number((quantity || 0) - (complementary || 0)) *
						Number(per_quantity_booked_price)
					: undefined
			const handler = setTimeout(() => {
				form.change(`${name}.booked_price`, booked_price)
			})
			return () => clearTimeout(handler)
		}
	}, [
		edited_booked_price,
		quantity,
		per_quantity_booked_price,
		form,
		name,
		complementary,
	])
	return (
		<TextInputField
			size="sm"
			name={`${name}.per_quantity_booked_price`}
			type="number"
			placeholder="1"
			style={{ maxWidth: "100px" }}
			readOnly={!quantity}
			help={
				<GetFieldValue<number | undefined> name={`${name}.booked_price`}>
					{({ value }) =>
						value && Number(quantity || 0) > 1 ? (
							<Text>
								x {(quantity || 0) - (complementary || 0)} ={" "}
								<Money currency={currency} amount={value} />
							</Text>
						) : null
					}
				</GetFieldValue>
			}
			onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
				const value = e.currentTarget.value ?? undefined
				form.change(`${name}.edited_booked_price` as never, true as never)
				form.change(
					`${name}.per_quantity_booked_price` as never,
					value as never
				)
			}}
		/>
	)
}

function ResetEditedGivenPriceFieldOnConfigChange({
	name,
	dates,
	ticketType,
	defaultCurrency,
}: {
	name: string
	dates?: Array<Date>
	ticketType?: TTravelActivityTicketType
	defaultCurrency: string
}) {
	const { value } =
		useFieldValue<
			Required<TTravelActivityDatewisePricesInputFieldValue>["ticket_tourist_configurations"][number]
		>(name)
	const previousValues = useRef({
		configuration: value.configuration,
		// on-mount, if we have dates but not date-wise-prices,
		// we set it to empty to ensure that we generate the date_wise_prices
		dates: dates?.length && !value.date_wise_prices?.length ? [] : dates,
		ticketType,
	})
	const form = useForm()
	useEffect(() => {
		if (
			previousValues.current.configuration?.id !== value.configuration?.id ||
			!areDatesSame(previousValues.current.dates, dates) ||
			previousValues.current.ticketType?.id !== ticketType?.id
		) {
			const date_wise_prices = !dates?.length
				? []
				: dates.map((date) => ({
						...(value.date_wise_prices?.find((data) =>
							isSame(data.date, date, "day")
						) || {
							currency: defaultCurrency,
							given_price: undefined,
							per_quantity_given_price: undefined,
							booked_price: undefined,
							per_quantity_booked_price: undefined,
							price: undefined,
							per_quantity_price: undefined,
						}),
						date,
						edited_given_price: false,
						edited_booked_price: false,
						saved_system_calculated_price: undefined,
						saved_manual_given_price: undefined,
					}))
			const handler = setTimeout(() => {
				form.change(`${name}.date_wise_prices`, date_wise_prices)
			})
			previousValues.current.configuration = value.configuration
			previousValues.current.dates = dates
			previousValues.current.ticketType = ticketType
			return () => clearTimeout(handler)
		}
	}, [value, form, name, dates, ticketType, defaultCurrency])
	return null
}

function areDatesSame(
	dates1?: Array<Date | undefined> | "",
	dates2?: Array<Date | undefined> | ""
): boolean {
	function datesToString(dates?: Array<Date | undefined> | "") {
		return dates
			? dates
					.map((d) =>
						d
							? d instanceof Date
								? startOf(d, "day").toISOString()
								: String(d)
							: "-"
					)
					.join(",")
			: ""
	}
	return datesToString(dates1) === datesToString(dates2)
}
